import React, { useEffect, useState } from 'react';
import { Trans } from '@lingui/react';
import { TooltipGhost, Input, PrimaryGhostButton } from '@qover/molecules';
import { Heading4 } from '@qover/typography';
import messages from './discountForm.messages';
import {
  ActionContainer,
  DiscountRow,
  InputContainer,
  PromotionText,
  SummaryContainer,
} from './discountForm.styles';

type DiscountFormProps = React.PropsWithChildren<{
  actionContainerClassName?: string;
  addDiscount?: (discountCode: string) => Promise<void>;
  removeDiscount?: (discountCode: string) => Promise<void>;
  small?: boolean;
  discountCode?: string;
  title?: JSX.Element;
  tooltip?: JSX.Element;
}>;

export const DiscountForm = (props: DiscountFormProps) => {
  const [discount, setDiscount] = useState<string>('');
  const [error, setError] = useState<React.ReactElement>();

  useEffect(() => {
    if (props.discountCode) {
      setDiscount(props.discountCode);
    }
  }, [props.discountCode]);

  const handleAddDiscount = () => {
    if (!discount) {
      setError(messages.error);
      return Promise.resolve();
    }

    if (props.addDiscount) {
      return props.addDiscount(discount).catch(() => {
        setError(messages.invalidDiscount);
      });
    }
  };

  const handleRemoveDiscount = () => {
    if (props.removeDiscount) {
      props.removeDiscount(discount);
    }
  };

  return (
    <>
      {props.discountCode ? (
        <div>
          {props.children || (
            <Heading4 as="div" className="font-bold">
              {messages.removeDiscount(
                props.discountCode,
                handleRemoveDiscount,
              )}
            </Heading4>
          )}
        </div>
      ) : (
        <>
          {props.title && (
            <DiscountRow>
              {props.title}
              {props.tooltip && <TooltipGhost>{props.tooltip}</TooltipGhost>}
            </DiscountRow>
          )}
          {props.addDiscount && (
            <>
              <InputContainer>
                <Input
                  data-test="discounts.input"
                  error={error}
                  id="discounts.input"
                  invalid={!!error}
                  label={messages.discount}
                  labelType="floating"
                  onChange={(e: React.ChangeEvent<HTMLButtonElement>) => {
                    setError(undefined);
                    setDiscount(e.target.value);
                  }}
                  small={props.small}
                  value={discount}
                />
              </InputContainer>
              <ActionContainer className={props.actionContainerClassName}>
                <PrimaryGhostButton
                  small={props.small}
                  type="button"
                  onClick={handleAddDiscount}
                >
                  {messages.actionLabel}
                </PrimaryGhostButton>
              </ActionContainer>
            </>
          )}
        </>
      )}
    </>
  );
};

export function SummaryDiscountForm(props: DiscountFormProps) {
  return (
    <SummaryContainer>
      <PromotionText>
        <Trans id="general.discount" defaults="PROMOTION" />
      </PromotionText>
      <DiscountForm {...props} />
    </SummaryContainer>
  );
}
